import { useCallback, useEffect, useRef } from "react"

export interface TimerItem<T> {
  timer: NodeJS.Timeout
  id: string
  data: T
}

export type TimerCallback<T> = (id: string, data: T) => Promise<void> | void

/**
 * Hook for managing debounced updates with a timer system
 *
 * @param onUpdate Callback function to execute when the timer expires
 * @param debounceMs Time in milliseconds to wait before executing the callback
 * @returns Functions to save an item (with debouncing) and clear all timers
 */
export function useUpdateTimers<T>(
  onUpdate: TimerCallback<T>,
  debounceMs: number = 250,
) {
  // Track update timers for each item
  const timers = useRef<Record<string, TimerItem<T>>>({})

  // Function to schedule an update with debouncing
  const scheduleUpdate = useCallback(
    (id: string, data: T, customDebounceMs?: number) => {
      // Clear any existing timer for this item
      if (timers.current[id]) {
        clearTimeout(timers.current[id].timer)
        delete timers.current[id]
      }

      // Use custom debounce time if provided, otherwise use default
      const effectiveDebounceMs = customDebounceMs ?? debounceMs

      // Create a new timer
      const timer = setTimeout(() => {
        onUpdate(id, data)
        delete timers.current[id]
      }, effectiveDebounceMs)

      // Store the timer and item data
      timers.current[id] = { timer, id, data }
    },
    [onUpdate, debounceMs],
  )

  // Function to clear all timers
  const clearAllTimers = useCallback(() => {
    Object.values(timers.current).forEach(({ timer }) => {
      clearTimeout(timer)
    })
    timers.current = {}
  }, [])

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      clearAllTimers()
    }
  }, [clearAllTimers])

  return {
    scheduleUpdate,
    clearAllTimers,
    getPendingUpdates: () => Object.values(timers.current),
  }
}
